import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import {
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileCsv,
  faFile,
} from '@fortawesome/pro-solid-svg-icons';
import PermissionDropdown from './PermissionDropdown';
import { DocumentWithPermission } from '../../hooks/useDocumentPermissions';
import { UserRoles } from '../../shared/constants';

interface DocumentListProps {
  documents: DocumentWithPermission[];
  onRemove: (id: string) => void;
  onUpdatePermissions: (id: string, permissions: UserRoles[]) => void;
}

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return <FontAwesomeIcon icon={faFilePdf} color="#E53935" size="lg" />;
    case 'doc':
    case 'docx':
      return <FontAwesomeIcon icon={faFileWord} color="#1E88E5" size="lg" />;
    case 'xls':
    case 'xlsx':
      return <FontAwesomeIcon icon={faFileExcel} color="#43A047" size="lg" />;
    case 'csv':
      return <FontAwesomeIcon icon={faFileCsv} color="#546E7A" size="lg" />;
    default:
      return <FontAwesomeIcon icon={faFile} color="#9E9E9E" size="lg" />;
  }
};

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  onRemove,
  onUpdatePermissions,
}) => {
  if (documents.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <FontAwesomeIcon icon={faFile} /> Documents
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, width: '100%' }}
      >
        <Table sx={{ width: '100%' }} aria-label="documents table">
          <TableHead sx={{ backgroundColor: '#f9fafb' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '350px' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => (
              <TableRow
                key={doc.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getFileIcon(doc.file.name)}
                    <Typography variant="body2">{doc.file.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatBytes(doc.file.size)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PermissionDropdown
                      selectedPermissions={doc.permissions}
                      onChange={(permissions) =>
                        onUpdatePermissions(doc.id, permissions)
                      }
                    />
                    <IconButton onClick={() => onRemove(doc.id)} size="small">
                      <FontAwesomeIcon icon={faTrashCan} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DocumentList;
